import React, { useCallback, useMemo } from 'react';
import { Combobox, useCombobox } from '@mantine/core';
import { Button } from '../Button/Button';
import Styled from './MultiSelect.styles';
import { HudIcon, IconType } from '../Icons/HudIcons';
import LongContentPopover from '../LongContentPopover/LongContentPopover';
import Tooltip from '../Tooltip/Tooltip';

export interface Option {
  value: string;
  label: string | React.ReactNode;
  icon?: IconType;
}

export interface Group {
  value: string;
  label: string | React.ReactNode;
  icon?: IconType;
  options: Option[];
}

export interface GroupOption extends Option {
  relatedOptionsValues: string[];
  disabledTooltipDesc?: string;
}

export const isGroup = (option: Option | Group): option is Group => {
  return (option as Group).options !== undefined;
};

export interface MultiSelectProps
  extends Pick<React.ComponentProps<typeof Combobox>, 'position' | 'offset'> {
  mainGroupOption?: GroupOption;
  options: (Option | Group)[];
  selectedOptions: string[];
  searchable?: boolean;
  searchTerm?: string;
  optionReverseEllipsis?: boolean;
  onChange: (selectedOptions: string[]) => void;
  onSearch?: (searchTerm: string) => void;
  onClose?: () => void;
  renderButton?: (props: { onClick: () => void }) => React.ReactElement;
  disabledOptions?: string[];
  fullWidth?: boolean;
}

interface SelectOptionProps extends Option {
  selected: boolean;
  reverseEllipsis?: boolean;
  disabled?: boolean;
  disabledTooltipDesc?: string;
}

const SelectOption = React.memo(
  ({
    value,
    label,
    icon,
    selected,
    reverseEllipsis,
    disabled,
  }: SelectOptionProps) => (
    <Styled.Option
      role="option"
      value={value}
      selected={selected}
      aria-selected={selected}
      disabled={disabled}
      aria-label={label as string}
    >
      <Styled.CheckedContainer>
        {selected && <HudIcon icon="other.check" size="18px" />}
      </Styled.CheckedContainer>
      {icon && <Styled.SelectIcon icon={icon} size="18px" />}
      <Styled.OptionLabel>
        <LongContentPopover reverseEllipsis={reverseEllipsis}>
          {label}
        </LongContentPopover>
      </Styled.OptionLabel>
    </Styled.Option>
  ),
);

const SelectOptionWithTooltip = React.memo(
  ({
    value,
    label,
    selected,
    reverseEllipsis,
    disabled,
    disabledTooltipDesc,
  }: SelectOptionProps) => {
    const option = (
      <SelectOption
        selected={selected}
        value={value}
        label={label}
        reverseEllipsis={reverseEllipsis}
        disabled={disabled}
      />
    );

    return disabled ? (
      <Tooltip description={disabledTooltipDesc}>{option}</Tooltip>
    ) : (
      option
    );
  },
);

const MultiSelect = ({
  mainGroupOption,
  options,
  selectedOptions,
  searchable = false,
  searchTerm,
  position,
  offset,
  optionReverseEllipsis,
  onChange,
  onSearch,
  onClose,
  renderButton,
  disabledOptions = [],
  fullWidth,
}: MultiSelectProps) => {
  const [expandedGroups, setExpandedGroups] = React.useState<string[]>([]);

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      onSearch?.('');
      onClose?.();
    },
  });

  const handleOptionSubmit = useCallback(
    (value: string) => {
      let newSelectedOptions: string[];
      if (mainGroupOption && value === mainGroupOption.value) {
        const isMainGroupSelected =
          mainGroupOption.relatedOptionsValues.length ===
            selectedOptions.length &&
          mainGroupOption.relatedOptionsValues.every((opt) =>
            selectedOptions.includes(opt),
          );

        newSelectedOptions = isMainGroupSelected
          ? selectedOptions.filter(
              (opt) => !mainGroupOption.relatedOptionsValues.includes(opt),
            )
          : [...new Set([...mainGroupOption.relatedOptionsValues])];
      } else {
        newSelectedOptions = selectedOptions.includes(value)
          ? selectedOptions.filter((option) => option !== value)
          : [...selectedOptions, value];
      }

      onChange(newSelectedOptions);
    },
    [mainGroupOption, selectedOptions, onChange],
  );

  const isMainOptionSelected = useMemo(() => {
    return (
      mainGroupOption &&
      mainGroupOption.relatedOptionsValues.length > 0 &&
      mainGroupOption.relatedOptionsValues.length === selectedOptions.length &&
      mainGroupOption.relatedOptionsValues.every((opt) =>
        selectedOptions.includes(opt),
      )
    );
  }, [mainGroupOption, selectedOptions]);

  const renderedOptions = useMemo(() => {
    return options.map((option) =>
      isGroup(option) ? (
        <Styled.Group>
          <Styled.GroupTitle
            onClick={() =>
              setExpandedGroups((prev) =>
                prev.includes(option.value)
                  ? prev.filter((group) => group !== option.value)
                  : [...prev, option.value],
              )
            }
          >
            <Styled.GroupCaret expanded={expandedGroups.includes(option.value)}>
              <HudIcon icon="other.arrow" size="8px" />
            </Styled.GroupCaret>
            {option.icon && <Styled.SelectIcon icon={option.icon} />}
            {option.label}
          </Styled.GroupTitle>
          {expandedGroups.includes(option.value) && (
            <Styled.GroupOptionsContainer>
              {option.options.map((groupOption) => (
                <SelectOption
                  key={groupOption.value}
                  value={groupOption.value}
                  label={groupOption.label}
                  icon={groupOption.icon}
                  selected={selectedOptions.includes(groupOption.value)}
                  reverseEllipsis={optionReverseEllipsis}
                  disabled={disabledOptions.includes(groupOption.value)}
                />
              ))}
            </Styled.GroupOptionsContainer>
          )}
        </Styled.Group>
      ) : (
        <SelectOption
          key={option.value}
          value={option.value}
          label={option.label}
          icon={option.icon}
          selected={selectedOptions.includes(option.value)}
          reverseEllipsis={optionReverseEllipsis}
          disabled={disabledOptions.includes(option.value)}
        />
      ),
    );
  }, [options, selectedOptions, expandedGroups, disabledOptions]);

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleOptionSubmit}
      withinPortal={false}
      position={position}
      offset={offset}
      unstyled
    >
      <Combobox.DropdownTarget>
        {renderButton ? (
          renderButton({ onClick: () => combobox.toggleDropdown() })
        ) : (
          <Button role="combobox" onClick={() => combobox.toggleDropdown()}>
            Open dropdown
          </Button>
        )}
      </Combobox.DropdownTarget>
      <Styled.Dropdown $fullWidth={fullWidth}>
        {searchable && (
          <Styled.Search
            placeholder="Search..."
            onChange={(event) => onSearch?.(event.currentTarget.value)}
            value={searchTerm}
          />
        )}
        <Styled.Options>
          <Styled.ScrollArea>
            {mainGroupOption && (
              <>
                <SelectOptionWithTooltip
                  key={mainGroupOption.value}
                  value={mainGroupOption.value}
                  label={mainGroupOption.label}
                  selected={isMainOptionSelected || false}
                  reverseEllipsis={optionReverseEllipsis}
                  disabled={mainGroupOption.relatedOptionsValues.length === 0}
                  disabledTooltipDesc={mainGroupOption.disabledTooltipDesc}
                />
                <Styled.Divider />
              </>
            )}
            {renderedOptions}
          </Styled.ScrollArea>
        </Styled.Options>
      </Styled.Dropdown>
    </Combobox>
  );
};

export default React.memo(MultiSelect);
